// Connexion bancaire du propriétaire (F-050, D-15) : lecture seule via un agrégateur, consentement hébergé par le
// prestataire, synchronisation, expiration et reconnexion. Toujours signalée « Simulateur » tant qu'aucun accès réel n'existe.
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { post, put } from "@core/api";
import { useGet } from "@core/hooks";
import { dateTime, today } from "@core/format";
import { Badge, Button, Card, Checkbox, Empty, ErrorBox, Input, Loading, MoneyInput, Notice, PageHeader, Select, SimBadge } from "@core/ui";
import { TransactionsToMatch } from "./rapprochement";
import { useAction, useG } from "./shared";

type Account = { id: string; label: string; iban_masked: string; selected: boolean };
type Connection = {
  id: string;
  provider: string;
  mode: string;
  bank_id: string;
  bank_name: string;
  status: string;
  consent_expires_at: string | null;
  last_sync_at: string | null;
  last_error: string;
  stale: boolean;
  consent_expires_soon: boolean;
  accounts: Account[];
};
type Overview = { provider: string; mode: string; banks: { id: string; name: string; note: string }[]; connections: Connection[]; pending_transactions: number; limit: number | null };

const STATUS: Record<string, { label: string; tone: "succes" | "attention" | "erreur" | "neutre" }> = {
  en_attente_consentement: { label: "Consentement en attente", tone: "attention" },
  active: { label: "Active", tone: "succes" },
  reconnexion_requise: { label: "Reconnexion requise", tone: "erreur" },
  erreur: { label: "En erreur", tone: "erreur" },
  revoquee: { label: "Accès révoqué", tone: "neutre" },
  refusee: { label: "Consentement refusé", tone: "neutre" },
};

export function BanquePage() {
  const { g, can } = useG();
  const nav = useNavigate();
  const q = useGet<Overview>(g("/bank"));
  const { busy, run } = useAction();
  const [bank, setBank] = useState("banque_simulee");
  const d = q.data;
  return (
    <div className="page">
      <PageHeader
        eyebrow="Gestion"
        title={
          <>
            Banque {d ? <SimBadge mode={d.mode} /> : null}
          </>
        }
        intro="Connectez en lecture seule le compte sur lequel vous recevez les loyers. Vos identifiants bancaires sont saisis chez le prestataire, jamais dans Bailivo, et aucun paiement n’est initié ni encaissé par la plateforme."
      />
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {d ? (
        <>
          <Notice tone="simulateur" title="Mode simulateur">
            Aucun prestataire bancaire n’est encore contractualisé (fournisseur à choisir, Bridge étant un candidat documenté). Les banques, comptes et opérations ci-dessous sont simulés ; l’import CSV et la saisie restent disponibles.
          </Notice>
          {can("finance") ? (
            <Card title="Connecter une banque">
              <div className="form-row">
                <Select label="Banque" value={bank} onChange={(e) => setBank(e.target.value)} options={d.banks.map((b) => ({ value: b.id, label: b.name }))} help={d.banks.find((b) => b.id === bank)?.note} />
                <Button
                  busy={busy === "connect"}
                  onClick={async () => {
                    const r = await run("connect", () => post<any>(g("/bank/connections"), { bank_id: bank }));
                    if (r?.consent_url) nav(r.consent_url);
                  }}
                >
                  Continuer vers le prestataire
                </Button>
              </div>
              {d.limit !== null ? <p className="small muted">Votre offre permet {d.limit} connexion(s) bancaire(s) (hypothèse tarifaire non validée).</p> : null}
            </Card>
          ) : null}
          {d.connections.length === 0 ? <Empty title="Aucune banque connectée">Vous pouvez aussi importer un relevé CSV depuis « Paiements ».</Empty> : null}
          {d.connections.map((c) => (
            <ConnectionCard key={c.id} c={c} />
          ))}
          <TransactionsToMatch />
        </>
      ) : null}
    </div>
  );
}

function ConnectionCard({ c }: { c: Connection }) {
  const { g, can } = useG();
  const nav = useNavigate();
  const { busy, run } = useAction();
  const [sim, setSim] = useState<{ amount: number | null; payer: string; label: string }>({ amount: null, payer: "", label: "VIR SEPA RECU" });
  const st = STATUS[c.status] ?? { label: c.status, tone: "neutre" as const };
  const selected = c.accounts.filter((a) => a.selected).map((a) => a.id);
  return (
    <Card
      title={
        <>
          {c.bank_name} <SimBadge mode={c.mode} />
        </>
      }
      actions={<Badge tone={st.tone}>{st.label}</Badge>}
    >
      <p className="small">
        Dernière synchronisation : {c.last_sync_at ? dateTime(c.last_sync_at) : "jamais"}
        {c.consent_expires_at ? ` · autorisation valable jusqu’au ${dateTime(c.consent_expires_at)}` : ""}
      </p>
      {c.stale ? <Notice tone="attention">Données bancaires périmées : synchronisez avant de relancer un locataire. Un retard de synchronisation n’est pas un impayé.</Notice> : null}
      {c.consent_expires_soon ? <Notice tone="attention">L’autorisation expire bientôt : renouvelez-la pour éviter une interruption.</Notice> : null}
      {c.last_error ? <Notice tone="erreur">{c.last_error}</Notice> : null}
      {c.accounts.length ? (
        <fieldset className="stack-sm">
          <legend>Comptes à synchroniser</legend>
          {c.accounts.map((a) => (
            <Checkbox
              key={a.id}
              label={`${a.label} · ${a.iban_masked}`}
              checked={a.selected}
              onChange={(v) => can("finance") && run(a.id, () => put(g(`/bank/connections/${c.id}/accounts`), { selected: v ? [...selected, a.id] : selected.filter((x) => x !== a.id) }))}
            />
          ))}
        </fieldset>
      ) : null}
      {can("finance") ? (
        <div className="gestion-actions">
          {c.status === "active" ? (
            <Button busy={busy === "sync"} onClick={() => run("sync", () => post<any>(g(`/bank/connections/${c.id}/sync`)), (r: any) => (r.ok ? `${r.new_transactions} nouvelle(s) opération(s) à rapprocher.` : r.message))}>
              Synchroniser
            </Button>
          ) : null}
          {c.status === "reconnexion_requise" || c.status === "erreur" || c.status === "refusee" || c.consent_expires_soon ? (
            <Button
              variant="secondaire"
              busy={busy === "reco"}
              onClick={async () => {
                const r = await run("reco", () => post<any>(g(`/bank/connections/${c.id}/reconnect`)));
                if (r?.consent_url) nav(r.consent_url);
              }}
            >
              Reconnecter la banque
            </Button>
          ) : null}
          {c.status !== "revoquee" ? (
            <Button variant="danger" busy={busy === "revoke"} onClick={() => run("revoke", () => post(g(`/bank/connections/${c.id}/revoke`)), "Accès révoqué.")}>
              Révoquer l’accès
            </Button>
          ) : null}
        </div>
      ) : null}
      {can("finance") && c.mode === "simulateur" && c.status !== "revoquee" && c.status !== "en_attente_consentement" && c.status !== "refusee" ? (
        <details>
          <summary>Outils du simulateur</summary>
          <div className="stack-sm">
            <div className="form-row">
              <MoneyInput label="Virement reçu (simulé)" cents={sim.amount} onChange={(v) => setSim({ ...sim, amount: v })} />
              <Input label="Payeur" value={sim.payer} onChange={(e) => setSim({ ...sim, payer: e.target.value })} />
              <Input label="Libellé" value={sim.label} onChange={(e) => setSim({ ...sim, label: e.target.value })} />
            </div>
            <div className="gestion-actions">
              <Button variant="secondaire" small disabled={!sim.amount} busy={busy === "simtx"} onClick={() => run("simtx", () => post(g(`/bank/connections/${c.id}/simulate`), { action: "virement", amount_cents: sim.amount, payer: sim.payer, label: sim.label, booked_on: today() }), "Virement simulé ajouté chez le prestataire : synchronisez pour le recevoir.")}>
                Simuler un virement reçu
              </Button>
              <Button variant="discret" small busy={busy === "simexp"} onClick={() => run("simexp", () => post(g(`/bank/connections/${c.id}/simulate`), { action: "expiration" }), "Expiration simulée : la prochaine synchronisation demandera une reconnexion.")}>
                Simuler l’expiration de l’autorisation
              </Button>
            </div>
          </div>
        </details>
      ) : null}
    </Card>
  );
}

/** Page hébergée par le prestataire (ici simulée) : le propriétaire autorise ou refuse l'accès en lecture. */
export function ConsentementPage() {
  const { g, to } = useG();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const session = params.get("session") ?? "";
  const connection = params.get("connexion") ?? "";
  const { busy, run, error } = useAction();
  const decide = async (decision: "accepte" | "refuse") => {
    const r = await run(decision, () => post<any>(g(`/bank/connections/${connection}/consent`), { session, decision }), decision === "accepte" ? "Accès autorisé : choisissez les comptes à synchroniser." : "Consentement refusé : aucune donnée n’est lue.");
    if (r) nav(to("gestion/banque"));
  };
  return (
    <div className="page">
      <div className="gestion-consent stack">
        <Notice tone="simulateur" title="Page simulée du prestataire">
          En production, cette étape se déroule sur le site du prestataire d’agrégation, pas sur Bailivo : c’est là que vous vous authentifiez auprès de votre banque.
        </Notice>
        <div className="gestion-consent-provider stack">
          <h1>Autoriser la lecture de vos comptes</h1>
          <p>Bailivo demande un accès en lecture seule à vos comptes, pour repérer les loyers reçus :</p>
          <ul>
            <li>liste des comptes et opérations des comptes que vous choisirez ;</li>
            <li>aucun virement ni prélèvement ne peut être initié ;</li>
            <li>autorisation limitée dans le temps, révocable à tout moment.</li>
          </ul>
          <ErrorBox error={error} />
          {session && connection ? (
            <div className="gestion-actions">
              <Button busy={busy === "accepte"} onClick={() => decide("accepte")}>
                Autoriser l’accès en lecture
              </Button>
              <Button variant="secondaire" busy={busy === "refuse"} onClick={() => decide("refuse")}>
                Refuser
              </Button>
            </div>
          ) : (
            <Notice tone="erreur">Lien de consentement incomplet. Relancez la connexion depuis la page Banque.</Notice>
          )}
        </div>
      </div>
    </div>
  );
}
